import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  CircularProgress,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FilterDropDown from '../filter-dropdown/FilterDropDown';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import { selectQlikCalcVariableByName } from '../../store/slice/qlikVariableSlice';
import { getVariableContentByName, setVariableByName } from '../../services/qlikVariableService';
import { applySelectMatch, clearFieldByName } from '../../services/qlikSelectionService';
import { COMPANY_ANNUAL_REV_FIELD, DATE_RANGE_FIELD, QLIK_APP_REF } from '../../constants';
import { gtm } from '../../services/gtm';
import { getQlikResources } from '../../services/getQlikResources';
import { setPdfReportBy, setPdfReportFor, setShowPdfReport } from '../../store/slice/uiPropsSlice';

export interface PdfReportModalProps {
  open: boolean;
  onClose: () => void;
  autoLaunch?: boolean;
}

const PdfReportModal: React.FC<PdfReportModalProps> = ({
  open,
  onClose,
  autoLaunch = false,
}) => {
  const dispatch = useDispatch();
  // State
  const [reportFor, setReportFor] = useState('');
  const [reportBy, setReportBy] = useState('');
  const [showLoader, setShowLoader] = useState(false);

  // Selectors
  const appSelections = useSelector((state: RootState) => state.qlikSelection.qlikAppSelections[QLIK_APP_REF]);
  const calcVariableName = 'calcVars';
  const qlikCalcVariables = useSelector((state: RootState) => selectQlikCalcVariableByName(state, calcVariableName));

  // Get selections for industry
  const selectionIndustry = useMemo(() => {
    if (!appSelections) return '';
    const industrySel = appSelections.find((sel: any) => sel.qField === 'Industry');
    return industrySel?.qSelected || '';
  }, [appSelections]);

  const selectionIndustryClassName = useMemo(() => {
    if (!appSelections) return '';
    const classDescSel = appSelections.find((sel: any) => sel.qField === 'Class Description');
    return classDescSel?.qSelected || '';
  }, [appSelections]);

  // Test print criteria - requires all fields
  const printCriteria = useMemo(() => {
    return !!(
      selectionIndustry &&
      selectionIndustryClassName &&
      reportFor &&
      reportBy &&
      qlikCalcVariables?.vQRevenue &&
      qlikCalcVariables?.vQRevenue > 0
    );
  }, [selectionIndustry, selectionIndustryClassName, reportFor, reportBy, qlikCalcVariables?.vQRevenue]);

  // Load variables on init
  useEffect(() => {
    const initialize = async () => {
        if (open) {
            await loadVariables();
            if (!autoLaunch) {
                clearFieldByName(QLIK_APP_REF, DATE_RANGE_FIELD);
            }
            gtm.formStep('Customize Report', 'Customize Report Started', 1, false, false);
        }
    }
    initialize();
  }, [open, autoLaunch]);

  // Auto-launch if requested
  useEffect(() => {
    if (open && autoLaunch && qlikCalcVariables) {
      handlePrint();
    }
  }, [open, autoLaunch, qlikCalcVariables]);

  const loadVariables = async () => {
    try {
          
        // Load variable names from config
        const varsConfig = await getQlikResources('qlik-variables');
        const recVarsNames1 = varsConfig?.recVarsNames1 || [];

        // Load each variable's content
        for (const varObj of recVarsNames1) {
        const varName = Object.keys(varObj)[0];
        const varConfig = varObj[varName];
        await getVariableContentByName(calcVariableName, varName, varConfig.appRef);
        }
    } catch (error) {
        console.error('Error loading variables:', error);
    }
  };

  const handleRevenueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    const value = raw === '' ? 0 : Number(raw);
    setVariableByName(calcVariableName, 'vQRevenue', value, QLIK_APP_REF, 'Customize Report');
  };

  const handleRecordCountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    const value = raw === '' ? 0 : Number(raw);
    setVariableByName(calcVariableName, 'vUserRecordCount', value, QLIK_APP_REF, 'Customize Report');
  };

  const handleReportForChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReportFor(event.target.value);
    dispatch(setPdfReportFor(event.target.value));
  };

  const handleReportByChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReportBy(event.target.value);
    dispatch(setPdfReportBy(event.target.value));
  };

  const handleReportForBlur = () => {
    gtm.formEvent(`Changed: Report prepared for: ${reportFor}`, 'Customize Report');
  };

  const handleReportByBlur = () => {
    gtm.formEvent(`Changed: Report prepared by: ${reportBy}`, 'Customize Report');
  };

  const handlePrint = async () => {
    if (!autoLaunch) {
      // Update variables if not auto-launched (marketplace users already passed variables)
      if (qlikCalcVariables?.vQRevenue) {
        setVariableByName(calcVariableName, 'vQRevenue', qlikCalcVariables.vQRevenue, QLIK_APP_REF, 'Customize Report');
      }
      if (qlikCalcVariables?.vUserRecordCount) {
        setVariableByName(calcVariableName, 'vUserRecordCount', qlikCalcVariables.vUserRecordCount, QLIK_APP_REF, 'Customize Report');
      }
    }

    dispatch(setShowPdfReport(true));
    setShowLoader(true);

    // Map vQRevenue to company revenue size bucket
    if (qlikCalcVariables?.vQRevenue) {
      if (qlikCalcVariables.vQRevenue < 25000000) {
        await applySelectMatch(QLIK_APP_REF, COMPANY_ANNUAL_REV_FIELD, 'Under $25M');
      } else if (qlikCalcVariables.vQRevenue < 150000000) {
        await applySelectMatch(QLIK_APP_REF, COMPANY_ANNUAL_REV_FIELD, '$25.1M to $150M');
      } else if (qlikCalcVariables.vQRevenue < 500000000) {
        await applySelectMatch(QLIK_APP_REF, COMPANY_ANNUAL_REV_FIELD, '$151M to $500M');
      } else {
        await applySelectMatch(QLIK_APP_REF, COMPANY_ANNUAL_REV_FIELD, 'Over $501M');
      }
    } else {
      await clearFieldByName(QLIK_APP_REF, COMPANY_ANNUAL_REV_FIELD);
    }

    // Adjust chart widths for print (similar to Angular logic)
    // Use the legacy Angular class name 'object-placeholder' which Qlik objects render with;
    // apply width to ALL matching elements in each section.
    const claimsPlaceholders = document.querySelectorAll<HTMLElement>('#report-claims .object-placeholder, #report-claims .object-placeholder');
    claimsPlaceholders.forEach(el => { el.style.width = '500px'; });
    // const ppiPlaceholders = document.querySelectorAll<HTMLElement>('#report-ppi .object-placeholder, #report-ppi .object-placeholder');
    // ppiPlaceholders.forEach(el => { el.style.width = '400px'; });

    // Wait for resize and then print
    setTimeout(() => {
      // Trigger Qlik resize if available
    //   if ((window as any).qlikGlobal?.resize) {
    //     (window as any).qlikGlobal.resize();
    //   }
      
      setTimeout(() => {
        setShowLoader(false);
        window.print();
        
        // Track completion
        gtm.formStep(
          'Customize Report',
          'Customize Report Complete',
          2,
          true,
          {
            industry: selectionIndustry,
            indClass: selectionIndustryClassName,
            annualRev: qlikCalcVariables?.vQRevenue,
            userRecords: qlikCalcVariables?.vUserRecordCount,
            reportBy: reportBy,
            reportFor: reportFor,
          }
        );
        
        dispatch(setShowPdfReport(false));
        
        onClose();
      }, 3000);
    }, 1500);
  };

  // Print handlers
  useEffect(() => {
    const beforePrint = () => {
      if ((window as any).qlikGlobal?.resize) {
        (window as any).qlikGlobal.resize();
      }
    };

    const afterPrint = async () => {
      await clearFieldByName(QLIK_APP_REF, COMPANY_ANNUAL_REV_FIELD);
    };

    window.addEventListener('beforeprint', beforePrint);
    window.addEventListener('afterprint', afterPrint);

    return () => {
      window.removeEventListener('beforeprint', beforePrint);
      window.removeEventListener('afterprint', afterPrint);
    };
  }, []);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" component="div" sx={{color: 'var(--mui-custom-grey)', fontStyle: 'italic'}}>
          Confirm your selections and generate your Industry report
        </Typography>
        <IconButton aria-label="close" onClick={onClose} sx={{ color: 'text.secondary' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 3 }}>
        <Grid container spacing={3}>
          {/* Report prepared for */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              required
              size='small'
              label="Report prepared for"
              placeholder="Client company name"
              value={reportFor}
              onChange={handleReportForChange}
              onBlur={handleReportForBlur}
              variant="outlined"
            />
          </Grid>

          {/* Report prepared by */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              required
              size='small'
              label="Report prepared by"
              placeholder="Agency name"
              value={reportBy}
              onChange={handleReportByChange}
              onBlur={handleReportByBlur}
              variant="outlined"
            />
          </Grid>

          {/* Industry */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <FilterDropDown
              formName="Customize Report"
              fieldName="Industry"
              fieldLabel="Industry"
              qlikSort="qSortByAscii"
              multiSelect={false}
              required
            />
          </Grid>

          {/* Industry Class */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <FilterDropDown
              formName="Customize Report"
              fieldName="Class Description"
              fieldLabel="Industry Class"
              qlikSort="qSortByAscii"
              multiSelect={false}
              required
            />
          </Grid>

          {/* Annual Revenue */}
          <Grid size={{ xs: 12, sm: 6 }}>
            {/* <TextField
              fullWidth
              required
              type="number"
              label="Annual Revenue ($)"
              placeholder="Enter Annual Revenue ($)"
              value={qlikCalcVariables?.vQRevenue || ''}
              onChange={handleRevenueChange}
              variant="outlined"
              helperText="Company estimated annual revenue in dollars"
            /> */}
            <FormControl fullWidth required>
                <InputLabel htmlFor="outlined-adornment-amount">Annual Revenue ($)</InputLabel>
                <OutlinedInput
                    size='small'
                    required
                    fullWidth
                    type='number'
                    id="outlined-adornment-amount"
                    startAdornment={<InputAdornment position="start">$</InputAdornment>}
                    color='primary'
                    value={qlikCalcVariables?.vQRevenue !== undefined ? qlikCalcVariables.vQRevenue : ''}
                    onChange={handleRevenueChange}
                    label={'Annual Revenue ($)'}
                />
            </FormControl>
          </Grid>

          {/* User Record Count */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              type="number"
              size='small'
              label="User Record Count"
              placeholder="Enter User Record Count"
              value={qlikCalcVariables?.vUserRecordCount || ''}
              onChange={handleRecordCountChange}
              variant="outlined"
              helperText="Estimated annual amount of user records stored by the company"
            />
          </Grid>

          {/* Date Range */}
          <Grid size={{ xs: 12, sm: 12 }} container justifyContent={{ xs: 'stretch', sm: 'center' }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FilterDropDown
                formName="Customize Report"
                fieldName="Date Range"
                fieldLabel="Date Range"
                qlikSort="qSortByLoadOrder"
                multiSelect={false}
                removeValues={['Rolling 12 Months','Current Year','Last Complete Year','All Available Data']}
              />
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', py: 2 }}>
        <Button
          variant="contained"
          onClick={handlePrint}
          disabled={!printCriteria || showLoader}
          sx={{ minWidth: 150, textTransform: 'none' }}
        >
          {showLoader && <CircularProgress size={16} sx={{ mr: 1 }} />}
          {showLoader ? 'Please wait...' : 'Print Now'}
        </Button>
      </DialogActions>

      <Box sx={{ px: 3, pb: 2 }}>
        <Typography variant="caption" color="error">
          * Required fields
        </Typography>
      </Box>
    </Dialog>
  );
};

export default PdfReportModal;
